import React, { useState, useEffect } from 'react';
import { WizardProvider, useWizard } from './context/WizardContext';
import { SubscriptionProvider, useSubscription } from './context/SubscriptionContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { SurveyProvider } from './context/SurveyContext';
import ProgressBar from './components/shared/ProgressBar';
import StepPreviewOverlay from './components/shared/StepPreviewOverlay';
import DesignManager from './components/DesignManager/DesignManager';
import { UpgradePage, UpgradeModal } from './components/Subscription';
import { AuthPage, AccountPage } from './components/Auth';
import LandingPage from './components/LandingPage';
import WizardF from './components/WizardF/WizardF';
import GapFinderWizard from './components/WizardGF/GapFinderWizard';
import SurveyResponsePage from './components/WizardF/Response/SurveyResponsePage';
import { FolderOpen, Save, BookOpen, Boxes, Shield, ClipboardList, Compass, BarChart2, Crown, Lock, Eye, X, ArrowRight, User, ChevronDown, FileText, Menu, Search } from 'lucide-react';
import logo from './assets/logo.png';

import Step0_Intake from './components/WizardA/Step0_Intake';
import Step1A_PhilosophicalDimensions from './components/WizardA/Step1A_PhilosophicalDimensions';
import Step1B_Paradigm from './components/WizardA/Step1B_Paradigm';
import Step2_TheoryApproach from './components/WizardA/Step2_TheoryApproach';
import Step3_MethodologicalChoice from './components/WizardA/Step3_MethodologicalChoice';
import Step4_Strategy from './components/WizardA/Step4_Strategy';
import Step5_TimeHorizon from './components/WizardA/Step5_TimeHorizon';
import Step6_Techniques from './components/WizardA/Step6_Techniques';

import B0_Introduction from './components/WizardB/B0_Introduction';
import B1_AnalysisApproach from './components/WizardB/B1_AnalysisApproach';
import B2_QualitativePlanning from './components/WizardB/B2_QualitativePlanning';
import B3_QuantitativePlanning from './components/WizardB/B3_QuantitativePlanning';
import B4_DataRequirements from './components/WizardB/B4_DataRequirements';
import B5_WizardFIntegration from './components/WizardB/B5_WizardFIntegration';
import B6_ReviewExport from './components/WizardB/B6_ReviewExport';

import C1_ExtractConstructs from './components/WizardC/C1_ExtractConstructs';
import C2_SelectLevels from './components/WizardC/C2_SelectLevels';
import C3_TheoreticalFoundations from './components/WizardC/C3_TheoreticalFoundations';
import C4_TheoryLinkage from './components/WizardC/C4_TheoryLinkage';
import C5_RelationshipMapping from './components/WizardC/C3_RelationshipMapping';
import C6_Assumptions from './components/WizardC/C4_Assumptions';
import C7_GeneratePropositions from './components/WizardC/C5_GeneratePropositions';
import C8_ValidateFramework from './components/WizardC/C6_ValidateFramework';
import C9_ExportDiagram from './components/WizardC/C7_ExportDiagram';
import C10_FrameworkSection from './components/WizardC/C8_FrameworkSection';

import DataCollectionWizard from './components/WizardD/DataCollectionWizard';

import E1_Overview from './components/WizardE/D1_Overview';
import E2_ResearchTeam from './components/WizardE/D2_ResearchTeam';
import E3_ProjectSynopsis from './components/WizardE/D3_ProjectSynopsis';
import E4_Participants from './components/WizardE/D4_Participants';
import E5_Recruitment from './components/WizardE/D5_Recruitment';
import E6_DataCollection from './components/WizardE/D6_DataCollection';
import E7_DataManagement from './components/WizardE/D7_DataManagement';
import E8_RiskAssessment from './components/WizardE/D8_RiskAssessment';
import E9_SpecialConsiderations from './components/WizardE/D9_SpecialConsiderations';
import E10_Dissemination from './components/WizardE/D10_Dissemination';
import EthicsReport from './components/WizardE/EthicsReport';

import FinalReport from './components/Report/FinalReport';
import FrameworkPage from './components/Framework/FrameworkPage';
import Footer from './components/Footer/Footer';
import PrivacyPolicy from './components/FooterPages/PrivacyPolicy';
import TermsOfUse from './components/FooterPages/TermsOfUse';
import FAQs from './components/FooterPages/FAQs';
import LearnBasics from './components/FooterPages/LearnBasics';
import ContactUs from './components/FooterPages/ContactUs';
import CookiePolicy from './components/FooterPages/CookiePolicy';
import CookieConsent from './components/CookieConsent/CookieConsent';
import AdminDashboard from './components/Admin/AdminDashboard';

function WizardContent() {
  const { 
    currentWizard, 
    currentStep,
    highestStep,
    goToStep,
    wizardAComplete, 
    wizardBComplete,
    wizardCComplete,
    dataCollectionComplete,
    wizardDComplete,
    frameworkComplete,
    switchWizard,
    currentDesignName,
    hasUnsavedChanges,
    saveCurrentDesign
  } = useWizard();

  const {
    currentTier,
    canAccessWizard,
    hasFeature,
    triggerUpgradeModal,
    getTierConfig,
    isInTrialMode,
    activeTrialFeature,
    endTrial,
    canSaveDesign,
    upgradeTier,
    getAllTiers
  } = useSubscription();

  // eslint-disable-next-line no-unused-vars
  const { currentUser, isAuthenticated, isLoading: authLoading, devSetRole } = useAuth();

  const [showDesignManager, setShowDesignManager] = useState(false);
  const [showFramework, setShowFramework] = useState(false);
  const [showUpgradePage, setShowUpgradePage] = useState(false);
  const [showAuthPage, setShowAuthPage] = useState(false);
  const [showAccountPage, setShowAccountPage] = useState(false);
  const [showAdminDashboard, setShowAdminDashboard] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showLandingPage, setShowLandingPage] = useState(true);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showDevPanel, setShowDevPanel] = useState(false);
  const [currentFooterPage, setCurrentFooterPage] = useState(null);
  const [previousViewState, setPreviousViewState] = useState(null);
  const [showMobileWarning, setShowMobileWarning] = useState(
    () => window.innerWidth < 768 && !sessionStorage.getItem('mobile_warning_dismissed')
  );

  const isAdmin = currentUser?.role === 'super_admin' || currentUser?.role === 'sub_admin';

  const handleFooterNavigate = (pageId) => {
    setPreviousViewState({
      showLandingPage,
      showFramework,
      showUpgradePage,
      showAuthPage,
      showAccountPage
    });
    setCurrentFooterPage(pageId);
    window.scrollTo(0, 0);
  };

  const handleFooterPageBack = () => {
    setCurrentFooterPage(null);
    if (previousViewState) {
      setShowLandingPage(previousViewState.showLandingPage);
      setShowFramework(previousViewState.showFramework);
      setShowUpgradePage(previousViewState.showUpgradePage);
      setShowAuthPage(previousViewState.showAuthPage);
      setShowAccountPage(previousViewState.showAccountPage);
      setPreviousViewState(null);
    } else {
      setShowLandingPage(true);
    }
    window.scrollTo(0, 0);
  };

  const renderFooterPage = () => {
    switch (currentFooterPage) {
      case 'privacy':
        return <PrivacyPolicy onBack={handleFooterPageBack} />;
      case 'cookies':
        return <CookiePolicy onBack={handleFooterPageBack} />;
      case 'terms':
        return <TermsOfUse onBack={handleFooterPageBack} />;
      case 'faqs':
        return <FAQs onBack={handleFooterPageBack} />;
      case 'learn-basics':
        return <LearnBasics onBack={handleFooterPageBack} />;
      case 'contact':
        return <ContactUs onBack={handleFooterPageBack} />;
      default:
        return null;
    }
  };

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768 && showMobileMenu) {
        setShowMobileMenu(false);
      }
      if (window.innerWidth >= 768) {
        setShowMobileWarning(false);
      } else if (!sessionStorage.getItem('mobile_warning_dismissed')) {
        setShowMobileWarning(true);
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [showMobileMenu]);

  const dismissMobileWarning = () => {
    sessionStorage.setItem('mobile_warning_dismissed', '1');
    setShowMobileWarning(false);
  };

  const tierConfig = getTierConfig();
  
  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };
  
  const handleAuthSuccess = () => {
    setShowAuthPage(false);
  };
  
  const handleUserMenuClick = () => {
    setShowUserMenu(!showUserMenu);
  };
  
  const handleAccountClick = () => {
    setShowAccountPage(true);
    setShowUserMenu(false);
    setShowUpgradePage(false);
    setShowFramework(false);
  };

  const handleWizardClick = (wizard) => {
    if (!canAccessWizard(wizard)) {
      triggerUpgradeModal(`wizard${wizard}`);
      return;
    }
    switchWizard(wizard);
    setShowLandingPage(false);
    setShowFramework(false);
    setShowUpgradePage(false);
    setShowAuthPage(false);
    setShowAccountPage(false);
  };

  const handleFrameworkClick = () => {
    if (!hasFeature('learnBasics')) {
      triggerUpgradeModal('learnBasics');
      return;
    }
    setShowFramework(true);
    setShowLandingPage(false);
    setShowUpgradePage(false);
    setShowAuthPage(false);
    setShowAccountPage(false);
  };

  const isWizardLocked = (wizard) => !canAccessWizard(wizard);
  const isFeatureLocked = (feature) => !hasFeature(feature);

  const wizardASteps = [
    Step0_Intake,
    Step1A_PhilosophicalDimensions,
    Step1B_Paradigm,
    Step2_TheoryApproach,
    Step3_MethodologicalChoice,
    Step4_Strategy,
    Step5_TimeHorizon,
    Step6_Techniques
  ];

  const wizardBSteps = [
    B0_Introduction,
    B1_AnalysisApproach,
    B2_QualitativePlanning,
    B3_QuantitativePlanning,
    B4_DataRequirements,
    B5_WizardFIntegration,
    B6_ReviewExport
  ];

  const wizardCSteps = [
    C1_ExtractConstructs,
    C2_SelectLevels,
    C3_TheoreticalFoundations,
    C4_TheoryLinkage,
    C5_RelationshipMapping,
    C6_Assumptions,
    C7_GeneratePropositions,
    C8_ValidateFramework,
    C9_ExportDiagram,
    C10_FrameworkSection
  ];

  const wizardESteps = [
    E1_Overview,
    E2_ResearchTeam,
    E3_ProjectSynopsis,
    E4_Participants,
    E5_Recruitment,
    E6_DataCollection,
    E7_DataManagement,
    E8_RiskAssessment,
    E9_SpecialConsiderations,
    E10_Dissemination
  ];

  let CurrentComponent;

  if (currentWizard === 'GF') {
    CurrentComponent = GapFinderWizard;
  } else if (currentWizard === 'A') {
    if (currentStep < wizardASteps.length) {
      CurrentComponent = wizardASteps[currentStep];
    } else {
      CurrentComponent = FinalReport;
    }
  } else if (currentWizard === 'B') {
    if (currentStep < wizardBSteps.length) {
      CurrentComponent = wizardBSteps[currentStep];
    } else {
      CurrentComponent = FinalReport;
    }
  } else if (currentWizard === 'C') {
    if (currentStep < wizardCSteps.length) {
      CurrentComponent = wizardCSteps[currentStep];
    } else {
      CurrentComponent = FinalReport;
    }
  } else if (currentWizard === 'D') {
    CurrentComponent = DataCollectionWizard;
  } else if (currentWizard === 'E') {
    if (currentStep < wizardESteps.length) {
      CurrentComponent = wizardESteps[currentStep];
    } else {
      CurrentComponent = EthicsReport;
    }
  } else if (currentWizard === 'F') {
    CurrentComponent = WizardF;
  }

  const handleQuickSave = () => {
    if (!canSaveDesign()) {
      return;
    }
    saveCurrentDesign();
  };
  
  const featureNames = {
    wizardGF: 'Research Gap Finder',
    wizardA: 'Research Methodology (Full Access)',
    wizardB: 'Data Analysis Wizard',
    wizardC: 'Framework Builder',
    wizardD: 'Data Collection Process',
    wizardE: 'Ethics Approval Wizard',
    wizardF: 'Survey Builder & Qualitative Analysis',
    learnBasics: 'Framework - Learn the Basics',
    compareMethodology: 'Compare Methodology'
  };
  
  const handleTrialStart = (feature) => {
    if (feature.startsWith('wizard')) {
      const wizard = feature.replace('wizard', '');
      switchWizard(wizard);
      setShowFramework(false);
      setShowUpgradePage(false);
    } else if (feature === 'learnBasics') {
      setShowFramework(true);
      setShowUpgradePage(false);
    }
  };

  return (
    <div className="app">
      {showMobileWarning && (
        <div className="mobile-warning-banner">
          <div className="mobile-warning-content">
            <div className="mobile-warning-icon">💻</div>
            <div className="mobile-warning-text">
              <strong>Best viewed on desktop or tablet</strong>
              <span>This research tool works best on a larger screen. Some features may be harder to use on a phone.</span>
            </div>
          </div>
          <button className="mobile-warning-dismiss" onClick={dismissMobileWarning}>
            Got it
          </button>
        </div>
      )}
      <header className="app-header">
        <div className="header-top-bar">
          <div className="header-left-section">
            <button 
              className="logo-btn" 
              onClick={() => setShowLandingPage(true)}
              title="Return to home page"
            >
              <img src={logo} alt="RMW Logo" className="header-logo" />
            </button>
            {isAuthenticated && (
              <div className="design-info">
                <span className="design-name-display" title={currentDesignName}>
                  {currentDesignName.length > 20 ? currentDesignName.slice(0, 20) + '...' : currentDesignName}
                  {hasUnsavedChanges && <span className="unsaved-dot" title="Unsaved changes"></span>}
                </span>
              </div>
            )}
          </div>
          <button 
            className="mobile-menu-btn"
            onClick={() => setShowMobileMenu(!showMobileMenu)}
            title="Menu"
          >
            {showMobileMenu ? <X size={24} /> : <Menu size={24} />}
          </button>
          
          <div className={`header-actions ${showMobileMenu ? 'mobile-open' : ''}`}>
            <div className="subscription-indicator">
              <span className={`subscription-badge ${isInTrialMode ? 'trial' : currentTier}`}>
                {isInTrialMode ? 'TRIAL' : tierConfig.name}
              </span>
            </div>
            <button
              className="header-btn upgrade-btn"
              onClick={() => { setShowUpgradePage(true); setShowLandingPage(false); setShowFramework(false); setShowAuthPage(false); setShowAccountPage(false); setShowAdminDashboard(false); setShowMobileMenu(false); setCurrentFooterPage(null); }}
              title="View subscription plans"
            >
              <Crown size={18} />
              <span>Upgrade</span>
            </button>
            <button 
              className={`header-btn ${!canSaveDesign() ? 'save-btn-disabled' : ''}`}
              onClick={() => { handleQuickSave(); setShowMobileMenu(false); }}
              title={canSaveDesign() ? "Save current design" : "Saving disabled during trial"}
              disabled={!canSaveDesign()}
            >
              <Save size={18} />
              <span>Save</span>
            </button>
            <button 
              className="header-btn" 
              onClick={() => {
                if (!isAuthenticated) {
                  setShowAuthPage(true);
                  setShowMobileMenu(false);
                } else {
                  setShowDesignManager(true);
                  setShowMobileMenu(false);
                }
              }}
              title={isAuthenticated ? "Manage designs" : "Sign in to manage designs"}
            >
              <FolderOpen size={18} />
              <span>Designs</span>
            </button>
            
            {!authLoading && (
              isAuthenticated ? (
                <div className="user-menu-container">
                  <button 
                    className="user-menu-trigger"
                    onClick={handleUserMenuClick}
                  >
                    <span className="user-avatar">{getInitials(currentUser?.displayName)}</span>
                    <span className="user-name">{currentUser?.displayName || currentUser?.username}</span>
                    <ChevronDown size={14} />
                  </button>
                  {showUserMenu && (
                    <div className="user-dropdown" onClick={() => setShowUserMenu(false)}>
                      <button onClick={() => { handleAccountClick(); setShowMobileMenu(false); }}>
                        <User size={16} /> My Account
                      </button>
                      {isAdmin && (
                        <button onClick={() => { setShowAdminDashboard(true); setShowUserMenu(false); setShowMobileMenu(false); }}>
                          <Shield size={16} /> Admin Dashboard
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <button 
                  className="header-btn login-btn"
                  onClick={() => { 
                    setShowLandingPage(false);
                    setShowUpgradePage(false);
                    setShowAccountPage(false);
                    setShowFramework(false);
                    setShowAuthPage(true); 
                    setShowMobileMenu(false); 
                  }}
                  title="Sign in or create account"
                >
                  <User size={18} />
                  <span>Sign In</span>
                </button>
              )
            )}
          </div>
        </div>
        
        {showMobileMenu && <div className="mobile-menu-overlay" onClick={() => setShowMobileMenu(false)} />}

        <div className="header-content">
          <h1>Research Methodology Wizard</h1>
          <p className="tagline">From Research Gaps, to Research Questions, Framework and Design</p>
        </div>
        
        <div className="wizard-tabs">
          <button
            className={`wizard-tab wizard-gf-tab ${currentWizard === 'GF' && !showFramework && !showUpgradePage ? 'active' : ''}`}
            onClick={() => handleWizardClick('GF')}
          >
            <Search size={18} className="tab-icon" />
            <span className="tab-label">Wizard A</span>
            <span className="tab-subtitle">Research Gap Finder</span>
          </button>

          <button
            className={`wizard-tab wizard-a-tab ${currentWizard === 'A' && !showFramework && !showUpgradePage ? 'active' : ''} ${wizardAComplete ? 'complete' : ''}`}
            onClick={() => handleWizardClick('A')}
          >
            <Compass size={18} className="tab-icon" />
            <span className="tab-label">Wizard B</span>
            <span className="tab-subtitle">Research Methodology</span>
            {wizardAComplete && <span className="complete-badge">✓</span>}
          </button>
          
          <button
            className={`wizard-tab wizard-b-tab ${currentWizard === 'B' && !showFramework && !showUpgradePage ? 'active' : ''} ${wizardBComplete ? 'complete' : ''} ${isWizardLocked('B') ? 'locked-wizard-tab' : ''}`}
            onClick={() => handleWizardClick('B')}
          >
            <BarChart2 size={18} className="tab-icon" />
            <span className="tab-label">Wizard C</span>
            <span className="tab-subtitle">Data Analysis</span>
            {isWizardLocked('B') && <Lock size={14} className="lock-icon-tab" />}
            {wizardBComplete && !isWizardLocked('B') && <span className="complete-badge">✓</span>}
          </button>

          <button
            className={`wizard-tab framework-tab ${showFramework && !showUpgradePage ? 'active' : ''} ${frameworkComplete ? 'complete' : ''} ${isFeatureLocked('learnBasics') ? 'locked-wizard-tab' : ''}`}
            onClick={handleFrameworkClick}
          >
            <BookOpen size={18} className="tab-icon" />
            <span className="tab-label">Framework</span>
            <span className="tab-subtitle">Learn the Basics</span>
            {isFeatureLocked('learnBasics') && <Lock size={14} className="lock-icon-tab" />}
            {frameworkComplete && !isFeatureLocked('learnBasics') && <span className="complete-badge">✓</span>}
          </button>

          <button
            className={`wizard-tab wizard-c-tab ${currentWizard === 'C' && !showFramework && !showUpgradePage ? 'active' : ''} ${wizardCComplete ? 'complete' : ''} ${isWizardLocked('C') ? 'locked-wizard-tab' : ''}`}
            onClick={() => handleWizardClick('C')}
          >
            <Boxes size={18} className="tab-icon" />
            <span className="tab-label">Wizard D</span>
            <span className="tab-subtitle">Framework Builder</span>
            {isWizardLocked('C') && <Lock size={14} className="lock-icon-tab" />}
            {wizardCComplete && !isWizardLocked('C') && <span className="complete-badge">✓</span>}
          </button>

          <button
            className={`wizard-tab wizard-d-tab ${currentWizard === 'D' && !showFramework && !showUpgradePage ? 'active' : ''} ${dataCollectionComplete ? 'complete' : ''} ${isWizardLocked('D') ? 'locked-wizard-tab' : ''}`}
            onClick={() => handleWizardClick('D')}
          >
            <ClipboardList size={18} className="tab-icon" />
            <span className="tab-label">Wizard E</span>
            <span className="tab-subtitle">Data Collection Process</span>
            {isWizardLocked('D') && <Lock size={14} className="lock-icon-tab" />}
            {dataCollectionComplete && !isWizardLocked('D') && <span className="complete-badge">✓</span>}
          </button>

          <button
            className={`wizard-tab wizard-e-tab ${currentWizard === 'E' && !showFramework && !showUpgradePage ? 'active' : ''} ${wizardDComplete ? 'complete' : ''} ${isWizardLocked('E') ? 'locked-wizard-tab' : ''}`}
            onClick={() => handleWizardClick('E')}
          >
            <Shield size={18} className="tab-icon" />
            <span className="tab-label">Wizard F</span>
            <span className="tab-subtitle">Ethics Approval</span>
            {isWizardLocked('E') && <Lock size={14} className="lock-icon-tab" />}
            {wizardDComplete && !isWizardLocked('E') && <span className="complete-badge">✓</span>}
          </button>

          <button
            className={`wizard-tab wizard-f-tab ${currentWizard === 'F' && !showFramework && !showUpgradePage ? 'active' : ''} ${isWizardLocked('F') ? 'locked-wizard-tab' : ''}`}
            onClick={() => handleWizardClick('F')}
          >
            <FileText size={18} className="tab-icon" />
            <span className="tab-label">Wizard G</span>
            <span className="tab-subtitle">Survey Builder & Qualitative Analysis</span>
            {isWizardLocked('F') && <Lock size={14} className="lock-icon-tab" />}
          </button>
        </div>
      </header>

      {isInTrialMode && (
        <div className="trial-banner">
          <div className="trial-banner-content">
            <Eye size={20} className="trial-banner-icon" />
            <span className="trial-banner-text">
              <strong>Trial Mode:</strong> You're previewing {featureNames[activeTrialFeature] || activeTrialFeature}. Saving and reports are disabled.
            </span>
          </div>
          <div className="trial-banner-actions">
            <button className="trial-end-btn" onClick={endTrial}>
              <X size={14} /> End Trial
            </button>
            <button className="trial-upgrade-btn" onClick={() => setShowUpgradePage(true)}>
              Upgrade Now <ArrowRight size={14} />
            </button>
          </div>
        </div>
      )}

      {!currentFooterPage && !showLandingPage && !showFramework && !showUpgradePage && !showAuthPage && !showAccountPage && <ProgressBar />}

      <main className="app-main">
        <div className="main-layout">
          <div className="content-container">
            {currentFooterPage ? (
              renderFooterPage()
            ) : showAdminDashboard ? (
              <AdminDashboard onBack={() => setShowAdminDashboard(false)} />
            ) : showLandingPage ? (
              <LandingPage 
                onGetStarted={() => { setShowLandingPage(false); setShowUpgradePage(true); }} 
                onUpgrade={() => { setShowLandingPage(false); setShowUpgradePage(true); }}
              />
            ) : showAuthPage ? (
              <AuthPage 
                onAuthSuccess={handleAuthSuccess} 
                onBack={() => setShowAuthPage(false)} 
              />
            ) : showAccountPage ? (
              <AccountPage 
                onBack={() => setShowAccountPage(false)} 
                onUpgrade={() => { setShowAccountPage(false); setShowUpgradePage(true); }}
              />
            ) : showUpgradePage ? (
              <UpgradePage onBack={() => setShowUpgradePage(false)} />
            ) : showFramework ? (
              <FrameworkPage onLaunchWizardC={() => setShowFramework(false)} />
            ) : (
              (() => {
                if (!CurrentComponent) return <div>Loading...</div>;
                const wizardIsComplete = currentWizard === 'A' ? wizardAComplete
                  : currentWizard === 'B' ? wizardBComplete
                  : currentWizard === 'C' ? wizardCComplete
                  : currentWizard === 'E' ? false
                  : false;
                const isPreviewMode = ['A','B','C','E'].includes(currentWizard)
                  && !wizardIsComplete
                  && currentStep > highestStep;
                if (isPreviewMode) {
                  return (
                    <StepPreviewOverlay
                      stepNumber={currentStep + 1}
                      onReturn={() => goToStep(highestStep)}
                    >
                      <CurrentComponent />
                    </StepPreviewOverlay>
                  );
                }
                return <CurrentComponent />;
              })()
            )}
          </div>
        </div>
      </main>

      <UpgradeModal 
        onNavigateToUpgrade={() => setShowUpgradePage(true)} 
        onTrialStart={handleTrialStart}
      />

      <Footer onNavigate={handleFooterNavigate} />

      <DesignManager 
        isOpen={showDesignManager} 
        onClose={() => setShowDesignManager(false)} 
      />

      <CookieConsent onViewPolicy={() => handleFooterNavigate('cookies')} />

      {process.env.NODE_ENV !== 'production' && (
        <div className="dev-panel-root">
          <button
            className="dev-panel-toggle"
            onClick={() => setShowDevPanel(p => !p)}
            title="Dev Tools — not visible in production"
          >
            🛠 DEV
          </button>
          {showDevPanel && (
            <div className="dev-panel">
              <div className="dev-panel-header">
                <span>🛠 Dev Tools</span>
                <button className="dev-panel-close" onClick={() => setShowDevPanel(false)}>✕</button>
              </div>

              <div className="dev-panel-section">
                <p className="dev-panel-label">Switch Subscription Tier</p>
                {getAllTiers().map((tier) => (
                  <button
                    key={tier.id}
                    className={`dev-panel-btn ${currentTier === tier.id ? 'dev-active' : ''}`}
                    onClick={() => upgradeTier(tier.id)}
                  >
                    {tier.name}
                    <span className="dev-price">{tier.displayPrice}</span>
                    {currentTier === tier.id && <span className="dev-check">✓</span>}
                  </button>
                ))}
              </div>

              <div className="dev-panel-section">
                <p className="dev-panel-label">Switch Admin Role</p>
                {[
                  { role: 'user', label: 'Regular User', icon: '👤' },
                  { role: 'sub_admin', label: 'Sub-Admin', icon: '🛡️' },
                  { role: 'super_admin', label: 'Super Admin', icon: '👑' },
                ].map(({ role, label, icon }) => (
                  <button
                    key={role}
                    className={`dev-panel-btn ${currentUser?.role === role ? 'dev-active' : ''}`}
                    onClick={() => devSetRole && devSetRole(role)}
                  >
                    {icon} {label}
                    {currentUser?.role === role && <span className="dev-check">✓</span>}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SurveyResponseWrapper() {
  const [surveyId, setSurveyId] = useState(null);

  useEffect(() => {
    const path = window.location.pathname;
    const match = path.match(/^\/survey\/(.+)$/);
    if (match) {
      setSurveyId(match[1]);
    }
  }, []);

  if (surveyId) {
    return <SurveyResponsePage surveyId={surveyId} />;
  }

  return <WizardContent />;
}

function App() {
  return (
    <AuthProvider>
      <SubscriptionProvider>
        <WizardProvider>
          <SurveyProvider>
            <SurveyResponseWrapper />
          </SurveyProvider>
        </WizardProvider>
      </SubscriptionProvider>
    </AuthProvider>
  );
}

export default App;
